<template>
  <div class="px-safe py-2">
    <div class="relative">
      <!-- Left scroll indicator -->
      <div
        class="absolute left-0 top-0 bottom-0 w-8 z-20 pointer-events-none flex items-center justify-center rounded-l-xl transition-opacity duration-200"
        :class="canScrollLeft ? 'opacity-100' : 'opacity-0'"
        style="background: linear-gradient(to right, var(--muted) 25%, transparent);">
        <svg class="h-3.5 w-3.5 text-muted-foreground" viewBox="0 0 12 12" fill="none">
          <path d="M7.5 2.5L4 6L7.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Right scroll indicator -->
      <div
        class="absolute right-0 top-0 bottom-0 w-8 z-20 pointer-events-none flex items-center justify-center rounded-r-xl transition-opacity duration-200"
        :class="canScrollRight ? 'opacity-100' : 'opacity-0'"
        style="background: linear-gradient(to left, var(--muted) 25%, transparent);">
        <svg class="h-3.5 w-3.5 text-muted-foreground" viewBox="0 0 12 12" fill="none">
          <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Scrollable segmented control -->
      <div ref="scrollContainer"
        class="overflow-x-auto scrollbar-hidden rounded-xl"
        @scroll="onScroll">
        <div ref="segmentedContainer"
          class="relative flex items-center bg-muted p-1"
          style="min-width: 100%; width: max-content;">

          <!-- Sliding active indicator -->
          <div
            class="absolute top-1 bottom-1 rounded-lg bg-card transition-all duration-300 ease-out segmented-indicator"
            :style="{
              left: `${indicatorLeft}px`,
              width: `${indicatorWidth}px`
            }" />

          <!-- Summary / Dashboard segment -->
          <button
            ref="dashboardSegment"
            class="segment-btn relative z-10 grow shrink-0 basis-auto flex items-center justify-center gap-1.5 rounded-lg py-2 px-3 text-xs font-medium transition-colors duration-200 whitespace-nowrap"
            :class="activeTab === 0 ? 'text-foreground' : 'text-muted-foreground'"
            @click="onSegmentClick(0, { name: 'Dashboard' })">
            <svg class="h-4 w-4 shrink-0 transition-colors duration-200" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
              :style="activeTab === 0 ? { color: getCenterColor(0) } : {}">
              <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" stroke-width="1.5" />
              <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" stroke-width="1.5" />
              <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" stroke-width="1.5" />
              <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" stroke-width="1.5" />
            </svg>
            <span class="truncate">{{ t('navigation.summary') }}</span>
          </button>

          <!-- Center segments -->
          <button
            v-for="(center, n) in centers"
            :key="center.id"
            ref="centerSegments"
            class="segment-btn relative z-10 grow shrink-0 basis-auto flex items-center justify-center rounded-lg py-2 px-3 text-xs font-medium transition-colors duration-200 whitespace-nowrap"
            :class="activeTab === n + 1 ? 'text-foreground' : 'text-muted-foreground'"
            @click="onSegmentClick(n + 1, { name: 'Center', params: { centerId: center.id } })">
            <div v-if="activeTab === n + 1"
              class="w-1.5 h-1.5 rounded-full shrink-0 mr-1.5 transition-all duration-200"
              :style="{ background: getCenterColor(n + 1) }" />
            <span class="truncate" style="max-width: 140px;">{{ center.preferences?.label || center.title }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, RouteLocationRaw } from 'vue-router';
import { Center } from '../../user';
import { Direction } from '../../GoManager';
import { getCenterColor } from '../../utils/CenterColors';

const { t } = useI18n();
const router = useRouter();

const props = defineProps<{
  centers: Center[],
  activeTab: number
}>();

const emits = defineEmits<{
  (event: 'transition', direction: Direction): void
}>();

const scrollContainer = ref<HTMLElement | null>(null);
const segmentedContainer = ref<HTMLElement | null>(null);
const dashboardSegment = ref<HTMLElement | null>(null);
const centerSegments = ref<HTMLElement[]>([]);

const indicatorLeft = ref(0);
const indicatorWidth = ref(0);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

/**
 * Returns the DOM element for the given segment index.
 * Index 0 = dashboard, index 1+ = centers.
 */
const getSegmentElement = (index: number): HTMLElement | null => {
  if (index === 0) {
    return dashboardSegment.value;
  }
  return centerSegments.value?.[index - 1] ?? null;
};

/**
 * Recalculate the indicator position using offsetLeft/offsetWidth
 * so it stays correct within the scrollable container.
 */
const updateIndicator = () => {
  const el = getSegmentElement(props.activeTab);
  if (!el) return;

  indicatorLeft.value = el.offsetLeft;
  indicatorWidth.value = el.offsetWidth;
};

/**
 * Track whether the scroll container can scroll in each direction
 * to show/hide the edge fade gradients.
 */
const updateScrollState = () => {
  const container = scrollContainer.value;
  if (!container) return;

  canScrollLeft.value = container.scrollLeft > 2;
  canScrollRight.value = container.scrollLeft + container.clientWidth < container.scrollWidth - 2;
};

/**
 * Scroll the container to center the active tab in view.
 */
const scrollToActive = (smooth = true) => {
  const el = getSegmentElement(props.activeTab);
  const container = scrollContainer.value;
  if (!el || !container) return;

  const elCenter = el.offsetLeft + el.offsetWidth / 2;
  const containerCenter = container.clientWidth / 2;
  const targetScroll = Math.max(0, elCenter - containerCenter);

  if (smooth) {
    container.scrollTo({ left: targetScroll, behavior: 'smooth' });
  } else {
    container.scrollLeft = targetScroll;
  }
};

const onScroll = () => {
  updateScrollState();
};

const onSegmentClick = (index: number, to: RouteLocationRaw) => {
  if (index === props.activeTab) return;

  // Emit transition direction before navigating
  emits('transition', index < props.activeTab ? 'right' : 'left');
  router.push(to);
};

const onResize = () => {
  updateIndicator();
  updateScrollState();
};

// Update indicator + scroll when the active tab changes
watch(() => props.activeTab, async () => {
  await nextTick();
  updateIndicator();
  scrollToActive();
});

// Update indicator when centers change (e.g. on first load)
watch(() => props.centers, async () => {
  await nextTick();
  updateIndicator();
  scrollToActive(false);
  updateScrollState();
}, { deep: true });

onMounted(() => {
  updateIndicator();
  scrollToActive(false); // immediate, no animation on mount
  updateScrollState();

  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
});
</script>

<style scoped>
.segmented-indicator {
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.06);
}

:is(.dark *) .segmented-indicator {
  box-shadow:
    0 1px 4px rgba(0, 0, 0, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.2);
}

.segment-btn {
  scroll-snap-align: center;
}
</style>
